import { APIService, APIError } from '../../../lib/api/APIService';

// Chat API class extending the base APIService
class ChatAPI extends APIService {
  async getChatSessions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/chat/sessions?${queryString}` : '/api/chat/sessions';
    return this.get(endpoint);
  }

  async createChatSession(title, agentType = null, projectId = null, selectedModel = null) {
    const body = { title: title || 'New chat' };
    if (agentType && ['planner', 'builder', 'coach'].includes(agentType)) {
      body.agent_type = agentType;
    }
    if (projectId) body.project_id = projectId;
    if (selectedModel) body.selected_model = selectedModel;
    return this.post('/api/chat/sessions', body);
  }

  async updateChatSession(sessionId, updates = {}) {
    return this.patch(`/api/chat/sessions/${sessionId}`, updates);
  }

  async getChatMessages(sessionId) {
    return this.get(`/api/chat/sessions/${sessionId}/messages`);
  }

  async postMessage(sessionId, message, autoMode = 'default', selectedModel = null, reasoningMode = false) {
    const payload = {
      message: message || '',
      auto_mode: autoMode && ['default', 'detailed', 'brief'].includes(autoMode) ? autoMode : 'default',
      reasoning_mode: reasoningMode
    };
    if (selectedModel) {
      payload.selected_model = selectedModel;
    }
    return this.post(`/api/chat/sessions/${sessionId}/messages`, payload);
  }

  /**
   * Stream assistant reply via SSE. Persists to DB on the server when complete
   * (same as postMessage). Callbacks: onToken(delta), onDone({ messages, bot_message, message }), onError(err).
   */
  async postMessageStream(sessionId, message, options = {}, callbacks = {}) {
    const {
      autoMode = 'default',
      selectedModel = null,
      reasoningMode = false,
      signal,
    } = options;
    const { onToken, onDone, onError, onPhase } = callbacks;

    const payload = {
      message: message || '',
      auto_mode: autoMode && ['default', 'detailed', 'brief'].includes(autoMode) ? autoMode : 'default',
      reasoning_mode: reasoningMode,
    };
    if (selectedModel) {
      payload.selected_model = selectedModel;
    }

    try {
      await this.ensureAuthFresh();
    } catch (err) {
      // Proactive renew is soft — do not end the session here. Continue with
      // whatever access token we still have; a real 401 below handles expiry.
    }

    const token = this.getAccessToken();
    if (!token) {
      this.notifySessionExpired();
      const err = new APIError('Authentication required', 401, { requiresLogin: true });
      onError?.(err);
      throw err;
    }

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    };
    headers.Authorization = `Bearer ${token}`;

    const response = await fetch(
      `${this.baseURL}/api/chat/sessions/${sessionId}/messages/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include',
        signal,
      }
    );

    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      let data = {};
      if (contentType.includes('application/json')) {
        data = await response.json().catch(() => ({}));
      } else {
        const text = await response.text().catch(() => '');
        if (text) data = { message: text };
      }
      if (response.status === 401 || response.status === 403) {
        // Try one terminal refresh before showing the overlay.
        try {
          await this.refreshAccessToken({ terminal: true });
          // Caller can retry; surface as auth error without wiping twice.
        } catch {
          this.notifySessionExpired();
        }
      }
      const err = new APIError(
        data.error || data.message || `HTTP error! status: ${response.status}`,
        response.status,
        data
      );
      onError?.(err);
      throw err;
    }

    if (!response.body || !contentType.includes('text/event-stream')) {
      const err = new APIError('Streaming not available', 500, {});
      onError?.(err);
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let donePayload = null;

    const parseSseBlock = (block, pendingDeltas) => {
      let eventType = 'message';
      let dataStr = '';
      block.split('\n').forEach((line) => {
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataStr += line.slice(5).trim();
        }
      });
      if (!dataStr) return;
      let data;
      try {
        data = JSON.parse(dataStr);
      } catch {
        data = { raw: dataStr };
      }
      if (eventType === 'token' && data.delta) {
        pendingDeltas.push(data.delta);
      } else if (eventType === 'phase') {
        onPhase?.(data.phase);
      } else if (eventType === 'ready') {
        // Connection alive; generation starting — no UI action required.
      } else if (eventType === 'done') {
        donePayload = data;
        onDone?.(data);
      } else if (eventType === 'error') {
        const err = new APIError(data.message || data.error || 'Stream failed', 400, data);
        onError?.(err);
        throw err;
      }
    };

    const flushPendingDeltas = (pendingDeltas) => {
      if (pendingDeltas.length === 0) return;
      onToken?.(pendingDeltas.join(''));
      pendingDeltas.length = 0;
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        const pendingDeltas = [];
        parts.forEach((block) => parseSseBlock(block, pendingDeltas));
        flushPendingDeltas(pendingDeltas);
      }
      if (buffer.trim()) {
        const pendingDeltas = [];
        parseSseBlock(buffer, pendingDeltas);
        flushPendingDeltas(pendingDeltas);
      }
    } catch (e) {
      if (e?.name === 'AbortError') {
        throw e;
      }
      if (!(e instanceof APIError)) {
        onError?.(e);
      }
      throw e;
    }

    if (!donePayload) {
      const err = new APIError('Stream ended without completion', 500, {});
      onError?.(err);
      throw err;
    }

    return donePayload;
  }

  async getAllSessions() {
    // exclude_pinned: this feeds "Recent" (via ChatAgentPanel's
    // refreshSidebar, which fetches Pinned separately with pinned_only).
    // Without it, pinned/onboarding rows consume slots in this 100-row
    // batch, showing in both groups and skewing the fullness check that
    // drives "Show more" pagination.
    return this.getChatSessions({ limit: 100, exclude_pinned: true });
  }

  async getSessionsByAgent(agentType) {
    const params = agentType ? { agent_type: agentType } : {};
    return this.getChatSessions(params);
  }

  async deleteSession(sessionId) {
    return this.delete(`/api/chat/sessions/${sessionId}`);
  }

  async pinSession(sessionId) {
    return this.post(`/api/chat/sessions/${sessionId}/pin`);
  }

  async unpinSession(sessionId) {
    return this.delete(`/api/chat/sessions/${sessionId}/pin`);
  }

  async getProjects() {
    return this.get('/api/chat/projects');
  }

  async createProject(name) {
    return this.post('/api/chat/projects', { name });
  }

  async updateProject(projectId, name) {
    return this.patch(`/api/chat/projects/${projectId}`, { name });
  }

  async deleteProject(projectId) {
    return this.delete(`/api/chat/projects/${projectId}`);
  }

  /** Ordered nodes/edges export for reasoning or Obsidian-style graph views. */
  async getGraph() {
    return this.get('/api/chat/graph');
  }

  async getChatHealth() {
    return this.get('/api/chat/health');
  }

  async getUsage() {
    return this.get('/api/chat/usage');
  }

  async uploadFile(file) {
    return this.upload('/api/chat/upload', file);
  }
}

// Create and export chat API instance
export const chatAPI = new ChatAPI();
